'use client';

import React, { useEffect } from 'react';

import { ErrorOverlay } from '@/components/ErrorOverlay';
import { useGlobalConfig } from '@/components/GlobalContext';

import { CraveErrorCode } from '@/enums/errorCodes';
import { getSystem } from '@/system';
import type { CraveError } from '@/types';
import { Errors, getCraveError } from '@/utils/errors';
import { getLocalization } from '@/utils/localization';

type NextErrorProps = {
  error: Error & { code: CraveErrorCode };
  reset: () => void;
};

export default function Error({ error, reset }: NextErrorProps) {
  const { userLanguage } = useGlobalConfig();
  const system = getSystem();

  useEffect(() => {
    if (system.logError) {
      system.logError(
        'other',
        'GENERIC_ERROR_4002',
        error.message || 'An unexpected error occurred',
        true
      );
    }
  }, [error, system]);

  let craveError: CraveError = getCraveError(
    CraveErrorCode.GENERIC_ERROR_4002,
    userLanguage
  );
  let buttonLabel = getLocalization(userLanguage)['errors']['error-button-ok'];
  if (craveError?.retry) {
    buttonLabel = getLocalization(userLanguage)['errors']['error-button-retry'];
  }

  if (Errors.has(error.code)) {
    craveError = getCraveError(error.code, userLanguage) as CraveError;
  }

  return (
    <html>
      <head>
        <meta
          name="description"
          content="Crave-Awe App - The best in streaming entertainment"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Something went wrong</title>
      </head>
      <body className="font-sans">
        <div role="alert" data-testid="error-boundary-container">
          {craveError ? (
            <ErrorOverlay
              error={craveError}
              buttonLabel={buttonLabel}
              onKeyPress={reset}
            />
          ) : null}
        </div>
      </body>
    </html>
  );
}
